<template>
  <div style="width: 100%">
    <div class="component-header my-2">
      <p>Select Work Type (Optional)</p>
      <v-spacer />
    </div>
    <div>
      <h5 class="primary--text my-5">Work Type</h5>
      <v-select
        v-model="selectedWorkType"
        :items="workTypeItems"
        item-text="name"
        :disabled="disabled"
        item-value="id"
        class="mt-4"
        ref="workTypeSelect"
        return-object
        outlined
        attach
        append-outer-icon="clear"
        @click:append-outer="confirmClearWorkType"
        @change="confirmChangeWorkType($event)"
      />
    </div>
    <div class="component-header my-2">
      <p>Job Steps</p>
      <v-spacer />
    </div>
    <v-form v-model="valid">
      <v-expansion-panels :disabled="disabled">
        <job-step
          v-for="(jobStep, i) in formData"
          :key="jobStep.id"
          :jobStep="jobStep"
          :index="i"
          :stepCount="formData.length"
          :selectedWorkType="selectedWorkType"
          :employeeItems="employeeItems"
          :hazardItems="hazardItems"
          :barrierNamesByHazardId="barrierNamesByHazardId"
          :rules="rules"
          @change="emitChanges"
          @remove="removeJobStep"
          @drag-start="dragStart"
          @drag-end="dragEnd"
          @drop="onDrop"
        />
      </v-expansion-panels>
      <v-row class="pt-3">
        <v-col>
          <v-btn
            class="primary"
            :disabled="disabled"
            @click.stop="newJobStep({})"
            >New Job Step</v-btn
          >
        </v-col>
      </v-row>
    </v-form>
    <dialog-message
      :showDialogMessage="showDialogMessage"
      displayCaption="Are you sure?"
      :displayText="displayText"
      :okAction="okAction"
      :cancelAction="cancelAction"
    />
  </div>
</template>
<script>
import dialogMessage from "@/components/submission/dialog-message.vue";
import JobStep from "@/components/submission/custom/job-step.vue";
import utils from "@/shared/Utils";
import appsignal from "@/plugins/appsignal";
import { debounce } from "@/utilities/debounce";
import { countRequired } from "@/utilities/job-steps";

// Progress recalculation walks the whole submission, so collapse bursts of
// edits (e.g. tabbing through several fields) into a single refresh.
const REFRESH_DELAY_MS = 300;

function addBreadcrumb(action, metadata = {}) {
  appsignal.addBreadcrumb({
    category: "Job Steps",
    action,
    metadata
  });
}

// This component owns the list of steps (add, remove, reorder, work type).
// Everything inside a single step lives in job-step.vue so that editing one
// step does not re-render the others. Keep per-step fields out of this
// template: anything read here (e.g. jobStep.name) would make every keystroke
// re-render the whole section again (SUPP-9155).
export default {
  name: "JobSteps",

  props: {
    jobSteps: Array,
    selectedHazards: Array,
    employees: Array,
    workType: Object,
    disabled: Boolean
  },
  components: {
    dialogMessage,
    JobStep
  },

  data() {
    return {
      valid: false,
      showDialogMessage: false,
      formData: [],
      rules: [v => !!v || "Required"],
      selectedWorkType: null,
      proposedWorkType: null,
      clearDialog: false
    };
  },

  computed: {
    displayText() {
      return this.clearDialog
        ? "All jobs steps will be removed if you clear the work type"
        : "All jobs steps will be replaced if you change the work type";
    },

    employeeItems() {
      return this.employees.map(this.toEmployeeItem);
    },

    barrierNamesByHazardId() {
      return this.$store.getters.getBarriers.reduce((acc, barrier) => {
        (acc[barrier.hazardId] = acc[barrier.hazardId] || []).push(
          barrier.name
        );
        return acc;
      }, {});
    },

    hazardItems() {
      return this.selectedHazards.map(this.toItem);
    },

    workTypeItems() {
      return this.$store.getters.getWorkTypes;
    }
  },

  beforeCreate() {
    // Defined before state init: the immediate jobSteps watcher calls it.
    this.refreshProgress = debounce(() => {
      this.$emit("refresh");
    }, REFRESH_DELAY_MS);
  },

  watch: {
    jobSteps: {
      immediate: true,
      handler: function(val) {
        // emitChanges() hands formData to the parent, which passes the same
        // array straight back as this prop. Rebuilding from it would only
        // re-render every step, so skip our own echo.
        if (val === this.formData) return;
        if (val && val.length > 0) {
          this.formData = val
            .map(this.toJobStepInput)
            .sort((a, b) => a.step - b.step);
        } else {
          if (this.formData.length === 0) {
            this.newJobStep();
          }
        }
        this.refreshProgress();
      }
    },
    workType: {
      immediate: true,
      deep: true,
      handler: function(val) {
        this.selectedWorkType = val;
      }
    }
  },

  methods: {
    dragStart(event, step) {
      event.dataTransfer.dropEffect = "move";
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("jobStep", step);
    },
    dragEnd() {
      this.formData.sort((a, b) => a.step - b.step);
      this.emitChanges();
    },
    onDrop(event, target) {
      const fromStep = parseInt(event.dataTransfer.getData("jobStep"), 10);
      if (!fromStep || fromStep === target.step) return;

      // Look steps up by value rather than assuming step N sits at index N-1,
      // so gaps or duplicates in saved step numbers can't throw.
      const ordered = [...this.formData].sort((a, b) => a.step - b.step);
      const fromIndex = ordered.findIndex(s => s.step === fromStep);
      const toIndex = ordered.indexOf(target);
      if (fromIndex === -1 || toIndex === -1) return;

      const [moved] = ordered.splice(fromIndex, 1);
      ordered.splice(toIndex, 0, moved);
      ordered.forEach((jobStep, index) => {
        jobStep.step = index + 1;
      });
      addBreadcrumb("Reorder job step", {
        from: fromStep,
        to: toIndex + 1,
        steps: this.formData.length
      });
    },
    cancelAction() {
      if (!this.clearDialog) {
        this.selectedWorkType = this.proposedWorkType;
      }
      this.showDialogMessage = false;
    },
    okAction() {
      this.clearDialog ? this.clearWorkType() : this.changeWorkType();
      this.showDialogMessage = false;
    },
    removeHazards(hazard) {
      addBreadcrumb("Remove hazard from job steps", { hazardId: hazard });
      const hazardOwner = this.formData.find(x =>
        x.jobStepHazards.find(y => y.hazardId === hazard)
      );

      if (hazardOwner && hazardOwner.jobStepHazards.length === 1) {
        this.removeJobStep(this.formData.findIndex(i => i === hazardOwner));
        return;
      }

      this.formData.forEach(e => {
        if (e.jobStepHazards) {
          e.jobStepHazards = e.jobStepHazards.filter(
            h => h.hazardId !== hazard
          );
        }
      });
      this.emitChanges();
    },
    getRequiredFields() {
      let count = 0;
      let totalRequired = 0;
      this.formData.forEach(e => {
        const { required, hasData } = countRequired(e);
        count += hasData;
        totalRequired += required;
      });
      return [count, totalRequired];
    },

    toEmployeeItem(e) {
      return {
        value: e.employee.id,
        text: `${e.employee.firstName} ${e.employee.lastName}`
      };
    },
    toItem({ id, name }) {
      return {
        value: id,
        text: name
      };
    },

    toJobStepInput(jobStep) {
      return {
        id: jobStep.id ? jobStep.id : utils.getUUID(),
        name: jobStep.name,
        step: jobStep.step,
        employeeIds: jobStep.employeeIds ? [...jobStep.employeeIds] : [],
        jobStepHazards: jobStep.jobStepHazards
          .map(this.toJobStepHazardInput)
          .sort((a, b) => a.position - b.position)
      };
    },

    toJobStepHazardInput(jobStepHazard) {
      return {
        id: jobStepHazard.id ? jobStepHazard.id : utils.getUUID(),
        source: jobStepHazard.source,
        position: jobStepHazard.position,
        hazardId: jobStepHazard.hazardId,
        barriers: jobStepHazard.barriers ? [...jobStepHazard.barriers] : []
      };
    },

    newJobStep(attrs = {}) {
      const totalSteps = this.formData.length + 1;
      this.formData.push({
        id: utils.getUUID(),
        name: "",
        step: totalSteps,
        employeeIds: [],
        jobStepHazards: [
          {
            id: utils.getUUID(),
            source: null,
            position: 1,
            hazardId: null,
            barriers: []
          }
        ],
        ...attrs
      });
      this.emitChanges();
    },

    removeJobStep(index) {
      addBreadcrumb("Remove job step", {
        step: index + 1,
        steps: this.formData.length
      });
      const jobSteps = [...this.formData];
      jobSteps.splice(index, 1);
      this.formData = jobSteps.map(this.remapStep);
      if (this.formData.length === 0) this.newJobStep();
      this.emitChanges();
    },

    remapStep(jobStep, index) {
      return {
        ...jobStep,
        step: index + 1
      };
    },

    emitChanges() {
      const riskAssessment = {
        jobSteps: this.formData,
        workType: this.selectedWorkType
      };
      this.$emit("change", riskAssessment);
      this.refreshProgress();
    },

    overwriteJobSteps() {
      if (this.selectedWorkType) {
        addBreadcrumb("Apply work type", {
          workTypeId: this.selectedWorkType.id,
          steps: this.selectedWorkType.jobSteps.length
        });
        this.formData.splice(0);
        for (let name of this.selectedWorkType.jobSteps) {
          this.newJobStep({ name });
        }
        this.emitChanges();
      }
    },

    confirmClearWorkType() {
      if (this.formData.length) {
        this.clearDialog = true;
        this.showDialogMessage = true;
      } else {
        this.clearWorkType();
      }
    },

    clearWorkType() {
      this.selectedWorkType = null;
      this.proposedWorkType = null;
      this.formData.splice(0);
      this.newJobStep();
      this.emitChanges();
      this.clearDialog = false;
    },

    confirmChangeWorkType() {
      if (
        this.proposedWorkType &&
        this.proposedWorkType !== this.selectedWorkType
      ) {
        this.clearDialog = false;
        this.showDialogMessage = true;
      } else {
        if (!this.proposedWorkType) this.changeWorkType();
      }
    },

    changeWorkType() {
      this.proposedWorkType = this.selectedWorkType;
      this.overwriteJobSteps();
      this.emitChanges();
    }
  }
};
</script>
